import React, { useMemo } from 'react';
import { Text, Group } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  LAYER_HEIGHT,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_FONT_FAMILY,
  FLASHBACK_COMPARISON_TITLE_FONT_SIZE,
  FLASHBACK_COMPARISON_MESSAGE_2_FONT_SIZE,
  FLASHBACK_COMPARISON_TITLE_FONT_STYLE_1,
  FLASHBACK_COMPARISON_TITLE_FONT_STYLE_2,
  FLASHBACK_COMPARISON_TITLE_LINE_HEIGHT_1,
  FLASHBACK_COMPARISON_TITLE_LINE_HEIGHT_2,
  FLASHBACK_COMPARISON_LOGO_TO_MESSAGE_SPACING,
  FLASHBACK_COMPARISON_MESSAGE_1_TO_MESSAGE_2_SPACING,
  FLASHBACK_COMPARISON_MESSAGE_2_TO_CARD_SPACING,
  FLASHBACK_COMPARISON_CARD_WIDTH,
  FLASHBACK_COMPARISON_CARD_HEIGHT,
  FLASHBACK_COMPARISON_CARD_CORNER_RADIUS,
  FLASHBACK_COMPARISON_BADGE_WIDTH,
  FLASHBACK_COMPARISON_BADGE_HEIGHT,
  FLASHBACK_COMPARISON_BADGE_ROTATION,
  FLASHBACK_COMPARISON_BADGE_OFFSET_X,
  FLASHBACK_COMPARISON_BADGE_OFFSET_Y
} from 'common/components/RewindModal/Konvas/constants';
import {
  IMG_BACKGROUND_MOVIE_SHARE,
  IMG_BACKGROUND_SERIES_SHARE
} from 'common/configuration/constants';

type ScreenComparisonProps = {
  data: {
    message_1?: string;
    message_2?: string;
    image_name?: string;
  };
  entities: any[];
  isSeries?: boolean;
};

const ShareableScreenComparison = React.forwardRef<any, ScreenComparisonProps>(
  ({ data, entities, isSeries = false }, ref) => {
    const message_1 = data?.message_1 ?? 'Message de comparaison 1 non trouvé.';
    const message_2 = data?.message_2 ?? 'Message de comparaison 2 non trouvé.';

    const contentEntity = entities?.[0]?.entities?.[0];
    const badgeUrl = data?.image_name
      ? `/commons/v9/allocine/flashback/${data.image_name}.png`
      : null;

    const backgroundImage = isSeries
      ? IMG_BACKGROUND_SERIES_SHARE
      : IMG_BACKGROUND_MOVIE_SHARE;

    const textLayout = useMemo(() => {
      const title1Height =
        FLASHBACK_COMPARISON_TITLE_FONT_SIZE *
        FLASHBACK_COMPARISON_TITLE_LINE_HEIGHT_1;
      const title2Height =
        FLASHBACK_COMPARISON_MESSAGE_2_FONT_SIZE *
        FLASHBACK_COMPARISON_TITLE_LINE_HEIGHT_2;

      const y_title1 = 0;
      const y_title2 =
        y_title1 +
        title1Height +
        FLASHBACK_COMPARISON_MESSAGE_1_TO_MESSAGE_2_SPACING;
      const y_card =
        y_title2 +
        title2Height +
        FLASHBACK_COMPARISON_MESSAGE_2_TO_CARD_SPACING * 4;

      return { y_title1, y_title2, y_card };
    }, []);

    const centerContentY =
      FLASHBACK_COVER_LOGO_TOP_MARGIN +
      FLASHBACK_COVER_LOGO_HEIGHT +
      FLASHBACK_COMPARISON_LOGO_TO_MESSAGE_SPACING;

    const centerX = LAYER_WIDTH / 2;
    const cardX = centerX - FLASHBACK_COMPARISON_CARD_WIDTH / 2;
    const cardY = textLayout.y_card;

    // Badge position: bottom-right of card (accounting for rotation)
    const marginForRotationX =
      (FLASHBACK_COMPARISON_BADGE_WIDTH * Math.sqrt(2) -
        FLASHBACK_COMPARISON_BADGE_WIDTH) /
      2;
    const marginForRotationY =
      (FLASHBACK_COMPARISON_BADGE_HEIGHT * Math.sqrt(2) -
        FLASHBACK_COMPARISON_BADGE_HEIGHT) /
      2;

    const badgeCenterXBase =
      cardX +
      FLASHBACK_COMPARISON_CARD_WIDTH -
      FLASHBACK_COMPARISON_BADGE_WIDTH / 2 +
      FLASHBACK_COMPARISON_BADGE_OFFSET_X;

    const badgeCenterX = Math.min(
      badgeCenterXBase,
      LAYER_WIDTH -
        FLASHBACK_COMPARISON_BADGE_WIDTH / 2 -
        marginForRotationX -
        BLOCK_MARGIN
    );

    const badgeCenterYBase =
      cardY +
      FLASHBACK_COMPARISON_CARD_HEIGHT -
      FLASHBACK_COMPARISON_BADGE_HEIGHT / 2 +
      FLASHBACK_COMPARISON_BADGE_OFFSET_Y;

    const maxYRelativeToGroup =
      LAYER_HEIGHT -
      centerContentY -
      FLASHBACK_COMPARISON_BADGE_HEIGHT / 2 -
      marginForRotationY -
      ALLOCINE_LOGO_HEIGHT -
      ALLOCINE_LOGO_BOTTOM_MARGIN;

    const badgeCenterY = Math.min(badgeCenterYBase, maxYRelativeToGroup);

    return (
      <ShareableStage ref={ref}>
        <FlashbackLayout
          backgroundColor={FLASHBACK_BACKGROUND_COLOR}
          backgroundOpacity={1}
          backgroundImage={backgroundImage}
          coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
          coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
          coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
          coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
          allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
          allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
          allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
        >
          <Group y={centerContentY}>
            <Text
              text={message_1}
              fontSize={FLASHBACK_COMPARISON_TITLE_FONT_SIZE}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={FLASHBACK_COMPARISON_TITLE_FONT_STYLE_1}
              lineHeight={FLASHBACK_COMPARISON_TITLE_LINE_HEIGHT_1}
              fill={FLASHBACK_TEXT_COLOR}
              width={LAYER_WIDTH - BLOCK_MARGIN * 4}
              x={BLOCK_MARGIN * 2}
              align="center"
              y={textLayout.y_title1}
            />
            <Text
              text={message_2}
              fontSize={FLASHBACK_COMPARISON_MESSAGE_2_FONT_SIZE}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={FLASHBACK_COMPARISON_TITLE_FONT_STYLE_2}
              lineHeight={FLASHBACK_COMPARISON_TITLE_LINE_HEIGHT_2}
              fill={FLASHBACK_TEXT_COLOR}
              width={LAYER_WIDTH - BLOCK_MARGIN * 4}
              x={BLOCK_MARGIN * 2}
              align="center"
              y={textLayout.y_title2}
            />
            {contentEntity?.poster?.path && (
              <KonvaImage
                path={contentEntity.poster.path}
                x={cardX}
                y={cardY}
                width={FLASHBACK_COMPARISON_CARD_WIDTH}
                height={FLASHBACK_COMPARISON_CARD_HEIGHT}
                cornerRadius={FLASHBACK_COMPARISON_CARD_CORNER_RADIUS}
              />
            )}
            {badgeUrl && (
              <KonvaImage
                path={badgeUrl}
                isLocal={false}
                x={badgeCenterX}
                y={badgeCenterY}
                width={FLASHBACK_COMPARISON_BADGE_WIDTH}
                height={FLASHBACK_COMPARISON_BADGE_HEIGHT}
                rotation={FLASHBACK_COMPARISON_BADGE_ROTATION}
                offsetX={FLASHBACK_COMPARISON_BADGE_WIDTH / 2}
                offsetY={FLASHBACK_COMPARISON_BADGE_HEIGHT / 2}
              />
            )}
          </Group>
        </FlashbackLayout>
      </ShareableStage>
    );
  }
);

ShareableScreenComparison.displayName = 'ShareableScreenComparison';
export default ShareableScreenComparison;
